import React, {useEffect, useState} from 'react'
import axios from 'axios'
import toaster from '../../../../Utils/toaster'
import {useFeatureAccess} from '../../../hooks/useFeatureAccess'

const API_URL = process.env.REACT_APP_API_URL

interface ConfigurationModel {
  code: number
  variable: string
  name: string
  value: string
  type: string
}

export function VOSReminderSettings() {
  const access = useFeatureAccess('emails')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [vosReminderEnabled, setVosReminderEnabled] = useState<ConfigurationModel | null>(null)
  const [vosReminderFrequency, setVosReminderFrequency] = useState<ConfigurationModel | null>(null)
  const [enabled, setEnabled] = useState(true)
  const [frequencyDays, setFrequencyDays] = useState(3)

  useEffect(() => {
    fetchConfigurations()
  }, [])

  const fetchConfigurations = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(`${API_URL}/Configurations`)
      const configurations = response.data?.data || []
      
      const enabledConfig = configurations.find(
        (c: ConfigurationModel) => c.variable === 'VOSReminderEnabled'
      )
      const frequencyConfig = configurations.find(
        (c: ConfigurationModel) => c.variable === 'VOSReminderFrequencyDays'
      )

      if (enabledConfig) {
        setVosReminderEnabled(enabledConfig)
        setEnabled(enabledConfig.value === '1')
      }

      if (frequencyConfig) {
        setVosReminderFrequency(frequencyConfig)
        setFrequencyDays(parseInt(frequencyConfig.value) || 3)
      }
    } catch (error) {
      console.error('Error fetching configurations:', error)
      toaster('error', 'Failed to load VOS reminder settings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (vosReminderEnabled) {
        const formData = new FormData()
        formData.append('code', String(vosReminderEnabled.code))
        formData.append('value', enabled ? '1' : '0')
        await axios.put(`${API_URL}/Configurations`, formData)
      }

      if (vosReminderFrequency) {
        const formData = new FormData()
        formData.append('code', String(vosReminderFrequency.code))
        formData.append('value', String(frequencyDays))
        await axios.put(`${API_URL}/Configurations`, formData)
      }

      toaster('success', 'VOS reminder settings saved successfully')
    } catch (error) {
      console.error('Error saving configurations:', error)
      toaster('error', 'Failed to save VOS reminder settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-10'>
        <span className='spinner-border spinner-border-lg'></span>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body'>
        <div className='row mb-6'>
          <div className='col-lg-6'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3'>Enable VOS Reminder Emails</label>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  id='vosReminderEnabled'
                  disabled={access !== 'edit'}
                />
                <label className='form-check-label text-gray-600' htmlFor='vosReminderEnabled'>
                  {enabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
              <div className='text-muted fs-7 mt-2'>
                When enabled, interpreters will receive email reminders to submit VOS forms for completed OSI appointments.
              </div>
            </div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-6'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3'>Reminder Frequency (Days)</label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={frequencyDays}
                onChange={(e) => setFrequencyDays(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
                max={30}
                disabled={!enabled || access !== 'edit'}
              />
              <div className='text-muted fs-7 mt-2'>
                How often to send reminder emails (in days) until the VOS form is submitted.
              </div>
            </div>
          </div>
        </div>

        {access === 'edit' && (
          <div className='row'>
            <div className='col-lg-6'>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

